import * as React from 'react'

import { Box, Flex, GridRow } from '~ui/components/layout/Box'
import { TSingleSpace } from '~ui/styleGuide/spaces'

type TProps = RequiredChildren & {
  headerContent: React.ReactElement
  /**
   * maximum width of the content
   */
  maxContentWidth: PropsOf<typeof Box>['maxWidth']
  /**
   * horizontal padding for desktop
   */
  contentPaddingHorizontal: TSingleSpace
  /**
   * background color of the container
   */
  containerBgColor?: PropsOf<typeof Flex>['bgColor']
  /**
   * minimum height of the header
   */
  desktopNavMinHeight: PropsOf<typeof GridRow>['minHeight']
  /**
   * whether the header should stay stuck on the screen while scrolling or not
   */
  stickyHeader?: boolean
  /**
   * background color of the screen header
   * also used to set header title color
   */
  headerBgColor?: PropsOf<typeof GridRow>['bgColor']
  /**
   * how much space should be left at the bottom of the page to enable to scroll the whole content visible (due to a fixed bottom panel)
   */
  pageBottomPadding?: PropsOf<typeof Box>['height']
  /**
   * content of the footer
   */
  footerContent?: React.ReactElement
}

/**
 * screen wrapper that renders its children inside a layout with header bar
 * usually used as a mobile version of a screen
 */
export const HeaderScreen: React.FC<TProps> = ({
  footerContent = false,
  children,
  headerContent,
  stickyHeader = false,
  headerBgColor = `bgColors.card`,
  containerBgColor,
  contentPaddingHorizontal,
  pageBottomPadding,
  maxContentWidth,
  desktopNavMinHeight,
}) => {
  return (
    <>
      <Flex
        bgColor={containerBgColor}
        flexDirection={`column`}
        minHeight={`100vh`}
        isolation={`isolate`}
        position={`relative`}
        zIndex={2}
      >
        <GridRow
          top={0}
          zIndex={5}
          gap={`smallMedium`}
          shadow={stickyHeader ? `bottomLong` : `none`}
          alignItems={`center`}
          bgColor={headerBgColor}
          position={stickyHeader ? `sticky` : `relative`}
        >
          <GridRow
            width={`100%`}
            alignItems={`center`}
            padding={{ horizontal: contentPaddingHorizontal, vertical: `extraSmall` }}
            maxWidth={maxContentWidth}
            minHeight={desktopNavMinHeight}
            margin={{ vertical: `none`, horizontal: `auto` }}
          >
            {headerContent}
          </GridRow>
        </GridRow>
        <Flex
          flexGrow={1}
          width={`100%`}
          maxWidth={maxContentWidth}
          margin={{ vertical: `none`, horizontal: `auto` }}
          flexDirection={`column`}
        >
          {children}
        </Flex>
      </Flex>

      {/* FOOTER */}
      {footerContent && (
        <Box bgColor={`bgColors.card`} isolation={`isolate`} position={`relative`} zIndex={1}>
          <Box
            maxWidth={maxContentWidth}
            margin={{ horizontal: `auto`, vertical: `none` }}
            padding={{ horizontal: contentPaddingHorizontal, vertical: `none` }}
          >
            {footerContent}
          </Box>
          {pageBottomPadding ? <Box height={pageBottomPadding}></Box> : null}
        </Box>
      )}
    </>
  )
}
